import type { CSSProperties } from "react";
import s from "./Dashboard.module.css";
import { formatKpiTarget, formatKpiValue, type GradedKpi } from "../../domain/kpis";
import { gradeColour, StateBadge } from "../../design/components";
import { t } from "../../i18n";

/**
 * One measure: value against a shown target, a grade, the state, and where
 * the number came from. When the platform cannot compute it, the tile says so
 * and gives the reason — it never shows zero. When the pack declares no target
 * the value is shown ungraded, and the tile says so.
 */
export function KpiTile({ kpi }: { kpi: GradedKpi }) {
  const colour = gradeColour(kpi.grade);
  const notComputable = kpi.value == null;
  return (
    <div className={s.kpi} style={{ "--kpi-c": colour } as CSSProperties} data-testid={`kpi-${kpi.key}`}>
      <div className={s.kpiLabel}>{t(kpi.label)}</div>
      <div className={s.kpiValue}>
        {notComputable ? (
          <span className={s.kpiNc}>{t("NOT COMPUTABLE")}</span>
        ) : (
          <>
            {formatKpiValue(kpi)}
            <span className={s.kpiUnit}>{unitWord(kpi.unit)}</span>
          </>
        )}
      </div>
      <div className={s.kpiGrade} aria-label={kpi.grade ? t("Grade {grade}", { grade: kpi.grade }) : t("Not graded")}>
        {kpi.grade ?? "—"}
      </div>
      {notComputable ? (
        <div className={s.kpiReason}>{kpi.reason ?? t("The platform did not return a value.")}</div>
      ) : (
        <div className={s.kpiFoot}>
          {kpi.target == null ? (
            <span title={t("The pack declares no target for this measure")}>
              {t("no target declared · not graded")}
            </span>
          ) : (
            <>
              <StateBadge state={kpi.state} />
              <span title={kpi.targetBasis ? t("Target basis: {basis}", { basis: kpi.targetBasis }) : t("Target")}>
                {t("target {sign} {value} {unit}", {
                  sign: kpi.direction === "up" ? "≥" : "≤",
                  value: formatKpiTarget(kpi),
                  unit: unitWord(kpi.unit),
                })}
              </span>
              {kpi.targetStatus === "proposed" && (
                <span
                  className={s.kpiProposed}
                  title={
                    kpi.targetBasis
                      ? t("Proposed, not yet agreed — {basis}", { basis: kpi.targetBasis })
                      : t("Proposed, not yet agreed")
                  }
                >
                  {t("PROPOSED")}
                </span>
              )}
              {/* build 6B: an agreed target says so, so proposed and agreed can be told apart at a glance */}
              {kpi.targetStatus === "agreed" && (
                <span
                  className={s.kpiProposed}
                  style={{ color: "var(--go)" }}
                  title={
                    kpi.targetSetBy
                      ? t("Agreed by {who}", { who: kpi.targetSetBy })
                      : kpi.targetBasis
                        ? t("Agreed — {basis}", { basis: kpi.targetBasis })
                        : t("Agreed")
                  }
                >
                  {t("AGREED")}
                </span>
              )}
            </>
          )}
          {kpi.history && kpi.history.length > 1 && <Sparkline values={kpi.history} colour={colour} />}
        </div>
      )}
    </div>
  );
}

/** A unit that is a word is translated; a symbol (%, h, min, kWh) is written as it is. */
const UNIT_WORDS: Record<string, string> = { open: "open", units: "units", "% plan": "% plan" };
function unitWord(unit: string): string {
  return Object.hasOwn(UNIT_WORDS, unit) ? t(UNIT_WORDS[unit]) : unit;
}

function Sparkline({ values, colour }: { values: number[]; colour: string }) {
  const w = 64;
  const h = 18;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pts = values
    .map(
      (v, i) =>
        `${((i / (values.length - 1)) * (w - 2) + 1).toFixed(1)},${(h - 2 - ((v - min) / span) * (h - 4)).toFixed(1)}`,
    )
    .join(" ");
  return (
    <svg className={s.spark} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <polyline
        points={pts}
        fill="none"
        stroke={colour}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
